'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  details: string[];
}

const processSteps: ProcessStep[] = [
{
  id: 1,
  title: 'Design & Programming',
  description: 'CAD/CAM integration with offline programming for optimized cutting paths and material utilization.',
  image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/FFC%20site%20images/Page2DesignProgr.webp",
  alt: 'Engineer working on CAD software designing precision metal parts on dual monitor workstation',
  details: [
  'DXF/DWG File Import',
  'Automatic Nesting Software',
  'Material Optimization',
  'Toolpath Simulation']

},
{
  id: 2,
  title: 'Precision Cutting',
  description: 'High-speed fiber laser cutting with real-time monitoring and quality control systems.',
  image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/FFC%20site%20images/Page2PreciCutting.webp",
  alt: 'Fiber laser cutting machine in operation with bright blue laser beam cutting through steel sheet with sparks',
  details: [
  '±0.1mm Tolerance',
  'Edge Quality Monitoring',
  'Automated Gas Control',
  'Real-Time Adjustments']

},
{
  id: 3,
  title: 'Forming & Bending',
  description: 'CNC press brake operations with precision angle control and repeatability across production runs.',
  image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/concept-to-completion/Bending%202.jpg",
  alt: 'Industrial CNC press brake machine bending metal sheet with precision hydraulic controls in manufacturing facility',
  details: [
  '±0.5° Angle Accuracy',
  'Offline Programming',
  'Multi-Axis Control',
  'Consistent Repeatability']

},
{
  id: 4,
  title: 'Quality Inspection',
  description: 'Comprehensive dimensional verification and surface quality checks before proceeding to next operations.',
  image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/FFC%20site%20images/Page2QualityInsp.jpg",
  alt: 'Quality inspector using digital calipers to measure precision metal component on inspection table',
  details: [
  'CMM Inspection',
  'Surface Quality Check',
  'Dimensional Verification',
  'Documentation & Traceability']

}];


export default function ProcessShowcase() {
  const [activeStep, setActiveStep] = useState(1);
  const [isHydrated, setIsHydrated] = useState(false);

  // Fix: useEffect instead of useState for hydration
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleStepClick = (id: number) => {
    if (!isHydrated) return;
    setActiveStep(id);
  };

  const currentStep = processSteps.find((step) => step.id === activeStep) || processSteps[0];

  return (
    <section className="lg:py-24 bg-background py-[70px]">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Cog6ToothIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-cta font-semibold text-primary">Process Excellence</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            From Concept to Completion
          </h2>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Our streamlined fabrication process ensures precision, efficiency, and quality at every stage of production.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {processSteps.map((step) =>
            <button
              key={step.id}
              onClick={() => handleStepClick(step.id)}
              className={`p-4 rounded-lg border-2 transition-all duration-base text-left ${
              activeStep === step.id ?
              'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'}`
              }>

                <div className="flex items-center space-x-3 mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-headline font-bold text-sm ${
                activeStep === step.id ?
                'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground'}`
                }>
                    {step.id}
                  </div>
                  <h3 className={`font-headline font-bold text-sm lg:text-base ${
                activeStep === step.id ? 'text-primary' : 'text-foreground'}`
                }>
                    {step.title}
                  </h3>
                </div>
              </button>
            )}
          </div>
          
          <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-64 lg:h-auto">
                <AppImage
                  key={currentStep.id}
                  src={currentStep.image}
                  alt={currentStep.alt}
                  className="w-full h-full object-cover" />

              </div>
              
              <div className="p-8 lg:p-12">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-headline font-bold text-primary-foreground">
                      {currentStep.id}
                    </span>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-headline font-bold text-foreground">
                    {currentStep.title}
                  </h3>
                </div>
                
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {currentStep.description}
                </p>
                
                <div className="space-y-3">
                  {currentStep.details.map((detail, index) =>
                  <div key={index} className="flex items-center space-x-3">
                      <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success flex-shrink-0" />
                      <span className="text-foreground">{detail}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}